import { useState } from "react";
import { AlertTriangle, ChevronDown } from "lucide-react";
import { cn } from "@/lib/cn";
import { groupShadeOptions } from "./shade-groups";
import { SingleSelectButtons } from "./SingleSelectButtons";

type ShadeOption = { id: string; system: string; code: string; compativel?: boolean };

type Props = {
  shades: ShadeOption[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  /** Cor que estava escolhida antes (edição do laboratório) — repassada pra grade marcar em âmbar. */
  originalId?: string | null;
};

/** Cor (VITA), classificada por sistema e por subgrupo (Tipo A/B/C/D, Grupo 1-5...).
 *
 *  Cada escala vem fechada: são dezenas de códigos por sistema, e deixá-los todos abertos empurrava
 *  o resto do formulário de pedido pra fora da tela. O cabeçalho mostra a cor já escolhida, então
 *  fechado não esconde informação — só as opções que ainda não interessam. Escolher fecha de volta,
 *  como um seletor; desmarcar mantém aberto, porque quem desmarcou está trocando de ideia. */
export function ShadeSelector({ shades, selectedId, onSelect, originalId = null }: Props) {
  const systemGroups = groupShadeOptions(shades);
  const [abertas, setAbertas] = useState<Set<string>>(new Set());

  const alternar = (systemLabel: string) =>
    setAbertas((prev) => {
      const next = new Set(prev);
      if (next.has(systemLabel)) next.delete(systemLabel);
      else next.add(systemLabel);
      return next;
    });

  const fechar = (systemLabel: string) =>
    setAbertas((prev) => {
      const next = new Set(prev);
      next.delete(systemLabel);
      return next;
    });

  return (
    <div className="space-y-2">
      {systemGroups.map((sg) => {
        const aberta = abertas.has(sg.systemLabel);
        const todas = sg.subgroups.flatMap((g) => g.options);
        const escolhida = todas.find((o) => o.id === selectedId);
        // A escala inteira é o que o laboratório declara — se nenhuma cor dela serve ao material
        // escolhido, avisa no cabeçalho. Avisar, e não esconder: com o catálogo meio preenchido,
        // esconder faria parecer que o laboratório não trabalha com aquela cor.
        const foraDoMaterial = todas.length > 0 && todas.every((o) => o.compativel === false);

        return (
          <div
            key={sg.systemLabel}
            className="rounded-lg border border-zinc-200 dark:border-border"
          >
            <button
              type="button"
              onClick={() => alternar(sg.systemLabel)}
              aria-expanded={aberta}
              className="flex w-full items-center gap-2 px-3 py-2 text-left transition-colors hover:bg-zinc-50 dark:hover:bg-muted/40"
            >
              <span className="flex-1 truncate text-[12px] font-medium text-zinc-700 dark:text-foreground/80">
                {sg.systemLabel}
              </span>
              {foraDoMaterial && (
                <span
                  title="O laboratório não indicou esta escala para o material escolhido. Você ainda pode usá-la."
                  className="inline-flex shrink-0 items-center gap-1 rounded-md bg-amber-50 px-1.5 py-0.5 text-[10.5px] font-semibold text-amber-700 dark:bg-amber-950/30 dark:text-amber-400"
                >
                  <AlertTriangle className="h-3 w-3" />
                  Não indicada
                </span>
              )}
              {escolhida && (
                <span className="shrink-0 rounded-md bg-brand-50 px-2 py-0.5 text-[12px] font-semibold text-brand-700 dark:bg-primary/15 dark:text-primary">
                  {escolhida.code}
                </span>
              )}
              <span className="shrink-0 text-[11px] font-semibold text-zinc-500 dark:text-muted-foreground">
                {aberta ? "Ocultar" : escolhida ? "Trocar" : "Escolher"}
              </span>
              <ChevronDown
                className={cn(
                  "h-3.5 w-3.5 shrink-0 text-zinc-400 transition-transform",
                  aberta && "rotate-180",
                )}
              />
            </button>

            {aberta && (
              <div className="space-y-1.5 border-t border-zinc-100 px-3 py-2 dark:border-border/60">
                {sg.subgroups.map((g) => (
                  <div key={g.label || sg.systemLabel} className="flex items-start gap-2">
                    {g.label && (
                      <span className="mt-1.5 w-14 shrink-0 text-[11px] text-zinc-400 dark:text-muted-foreground">
                        {g.label}
                      </span>
                    )}
                    <div className="min-w-0 flex-1">
                      <SingleSelectButtons
                        options={g.options.map((o) => ({ id: o.id, label: o.code }))}
                        selectedId={selectedId}
                        onSelect={(id) => {
                          onSelect(id);
                          if (id !== selectedId) fechar(sg.systemLabel);
                        }}
                        minWidthPx={56}
                        originalId={originalId}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
